import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { Branch, ReportType } from '../../types';

interface ReportFiltersProps {
  // Filter values
  startDate: Date | null;
  endDate: Date | null;
  selectedBranchId: string;
  selectedReportType: ReportType;
  
  // Filter handlers
  onStartDateChange: (date: Date | null) => void;
  onEndDateChange: (date: Date | null) => void;
  onBranchChange: (branchId: string) => void;
  onReportTypeChange: (reportType: ReportType) => void;
  
  // Action handlers
  onGenerateReport: () => void;
  
  // State
  loading: boolean;
  branches: Branch[];
}

const ReportFilters: React.FC<ReportFiltersProps> = ({
  startDate,
  endDate,
  selectedBranchId,
  selectedReportType,
  onStartDateChange,
  onEndDateChange,
  onBranchChange,
  onReportTypeChange,
  onGenerateReport,
  loading,
  branches,
}) => {
  const reportTypeOptions = [
    { value: ReportType.DAILY_ORDERS, label: 'Daily Orders Report' },
    { value: ReportType.SALES_SUMMARY, label: 'Sales Summary Report' },
    { value: ReportType.FINANCIAL_SETTLEMENT, label: 'Financial Settlement Report' },
    { value: ReportType.ALL_DAY_CHECKS, label: 'All Day Checks' },
  ];
  // Force refresh

  return (
    <Box className="no-print" sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {/* Report Type Selector */}
        <Box sx={{ flex: '1 1 250px' }}>
          <FormControl fullWidth>
            <InputLabel id="report-type-select-label">Report Type</InputLabel>
            <Select
              labelId="report-type-select-label"
              id="report-type-select"
              value={selectedReportType}
              label="Report Type"
              onChange={(e) => onReportTypeChange(e.target.value as ReportType)}
            >
              {reportTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Start Date */}
        <Box sx={{ flex: '1 1 250px' }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={selectedReportType === ReportType.ALL_DAY_CHECKS ? "Select Date" : "Start Date"}
              value={startDate}
              onChange={(date) => {
                onStartDateChange(date);
                if (selectedReportType === ReportType.ALL_DAY_CHECKS) {
                  onEndDateChange(date);
                }
              }}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>
        </Box>
        
        {/* End Date */}
        {selectedReportType !== ReportType.ALL_DAY_CHECKS && (
          <Box sx={{ flex: '1 1 250px' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={onEndDateChange}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Box>
        )}
        
        {/* Branch Selector */}
        <Box sx={{ flex: '1 1 250px' }}>
          <FormControl fullWidth>
            <InputLabel id="branch-select-label">Branch (Optional)</InputLabel>
            <Select
              labelId="branch-select-label"
              id="branch-select"
              value={selectedBranchId}
              label="Branch (Optional)"
              onChange={(e) => onBranchChange(e.target.value as string)}
            >
              <MenuItem value="">
                <em>All Branches</em>
              </MenuItem>
              {branches.map((branch) => (
                <MenuItem key={branch.id} value={branch.id}>
                  {branch.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        
        {/* Generate Button */}
        <Box sx={{ flex: '1 1 250px' }}>
          <Button 
            variant="contained" 
            color="primary" 
            fullWidth 
            onClick={onGenerateReport}
            disabled={loading || !startDate || !endDate}
            sx={{ height: '56px' }}
          >
            {loading ? <CircularProgress size={24} /> : 'Generate Report'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ReportFilters;